import { useEffect, useState } from 'react';

import { getCardActions } from '../services/cardActions';
import { normalizeId } from '../utils/ids';
import { getSessionKey, onSessionChange, useSessionUserId } from '../utils/session';

/**
 * A collection's card actions, for the Actions group of every item menu
 * (KPR-551). Those menus are many and per row — a table mounts one action set
 * per row — so the list is fetched ONCE per collection and shared (the
 * useDuplicateProfiles idiom), never once per row: the API answers the whole
 * collection in one request (GET …/card-actions) and each row filters it by
 * its card. In-flight requests are shared too, an entry older than STALE_MS is
 * re-read by the next surface that mounts, and a failed load is not
 * remembered — the menu simply has no Actions group.
 *
 * The cache is PER ACCOUNT (the app signs out without a reload): an entry
 * carries the session it was fetched under and is never served to another,
 * and onSessionChange clears it.
 *
 * Returns `{ actions }`, the API's rows for the collection (frozen, stable
 * between renders).
 */

const EMPTY = Object.freeze([]);
const STALE_MS = 60 * 1000;
const cache = new Map();

function entryFor(id) {
	const hit = cache.get(id);
	if (!hit) return null;
	if (hit.owner === getSessionKey()) return hit;
	cache.delete(id);
	return null;
}

onSessionChange(() => { cache.clear(); });

function load(id) {
	const hit = entryFor(id);
	if (hit && Date.now() - hit.at < STALE_MS) return hit.promise;
	const entry = { at: Date.now(), list: hit?.list || EMPTY, promise: null, owner: getSessionKey() };
	entry.promise = Promise.resolve()
		.then(() => getCardActions(id))
		.then(list => {
			if (entry.owner !== getSessionKey()) {
				if (cache.get(id) === entry) cache.delete(id);
				return EMPTY;
			}
			entry.list = Object.freeze(Array.isArray(list) ? list : []);
			return entry.list;
		})
		.catch(() => {
			if (cache.get(id) === entry) cache.delete(id);
			return EMPTY;
		});
	cache.set(id, entry);
	return entry.promise;
}

/** Test seam: forget every collection. */
export function resetCardActionsCache() {
	cache.clear();
}

export default function useCardActions(collectionId, { enabled = true } = {}) {
	const id = enabled ? normalizeId(collectionId) : '';
	const who = useSessionUserId();
	const [state, setState] = useState(() => ({ id, who, actions: (id && entryFor(id)?.list) || EMPTY }));

	useEffect(() => {
		if (!id) return undefined;
		let active = true;
		const put = list => setState(prev => (prev.id === id && prev.who === who && prev.actions === list ? prev : { id, who, actions: list }));
		put(entryFor(id)?.list || EMPTY);
		load(id).then(list => { if (active) put(list); });
		return () => { active = false; };
	}, [id, who]);

	return { actions: id && state.id === id && state.who === who ? state.actions : EMPTY };
}
